import React from "react";

import { useIntl } from "react-intl";

import { EntityTypesSupportingRoutes } from "@mapmycustomers/shared/types/entity";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { MAX_ROUTE_STOPS } from "@app/util/consts";

import StepProps from "../../type/StepProps";
import Footer from "../Footer";
import Counter, { messages } from "../Footer/Counter";

import styles from "./Adjust.module.scss";

interface Props extends StepProps {
  entityType: EntityTypesSupportingRoutes;
  hasInvalidStops: boolean;
  stopsCount: number;
}

const AdjustFooter: React.FC<Props> = ({
  entityType,
  hasInvalidStops,
  onBack,
  onNext,
  stopsCount,
}) => {
  const intl = useIntl();

  return (
    <Footer
      counter={
        <Counter
          comment={intl.formatMessage(messages.ok, { max: MAX_ROUTE_STOPS + 1 })}
          count={stopsCount}
        />
      }
    >
      <Button onClick={onBack} type="text">
        {intl.formatMessage({
          id: "activities.calendarView.routeBuilder.steps.adjust.footer.back",
          defaultMessage: "Back",
          description: "Back button in Adjust step in Route Builder in Activities Calendar view",
        })}
      </Button>
      <Popconfirm
        cancelText={intl.formatMessage({
          id: "activities.calendarView.routeBuilder.steps.adjust.footer.confirmProceed.cancel",
          defaultMessage: "Wait",
          description:
            "Cancel button in confirmation to proceed when some stops have no location on Adjust step in Route Builder in Activity Calendar view",
        })}
        description={
          <div className={styles.footerConfirmationTitle}>
            {intl.formatMessage({
              id: "activities.calendarView.routeBuilder.steps.adjust.footer.confirmProceed.description",
              defaultMessage:
                "Any stops without valid associations will not be included in your route.",
              description:
                "Confirm to proceed when some stops have no location on Adjust step in Route Builder in Activity Calendar view",
            })}
          </div>
        }
        okText={intl.formatMessage({
          id: "activities.calendarView.routeBuilder.steps.adjust.footer.confirmProceed.ok",
          defaultMessage: "I'm sure",
          description:
            "Ok button in confirmation to proceed when some stops have no location on Adjust step in Route Builder in Activity Calendar view",
        })}
        onConfirm={onNext}
        title={
          <div className={styles.footerConfirmationTitle}>
            {intl.formatMessage(
              {
                id: "activities.calendarView.routeBuilder.steps.adjust.footer.confirmProceed.title",
                defaultMessage:
                  "Are you sure you want to proceed without adding associated {entityType, select, accounts {Companies} contacts {People} other {Records}}?",
                description:
                  "Confirm to proceed when some stops have no location on Adjust step in Route Builder in Activity Calendar view",
              },
              { entityType }
            )}
          </div>
        }
        trigger={hasInvalidStops ? ["click"] : []}
      >
        <Button
          disabled={stopsCount > MAX_ROUTE_STOPS || stopsCount === 0}
          onClick={hasInvalidStops ? undefined : onNext}
          type="primary"
        >
          {intl.formatMessage({
            id: "activities.calendarView.routeBuilder.steps.adjust.footer.next",
            defaultMessage: "Next step",
            description: "Next button in Adjust step in Route Builder in Activities Calendar view",
          })}
        </Button>
      </Popconfirm>
    </Footer>
  );
};

export default AdjustFooter;
